import { line as d3Line, curveMonotoneX } from 'd3-shape';
import { Key } from 'react';

interface GradientStop {
  offset: string;
  color: string;
}

interface CustomGradientLayerProps {
  innerHeight: number;
  innerWidth: number;
  series: any;
  gradientStops: GradientStop[];
  getColorForValue: (value: number) => string;
}

export const CustomGradientLayer = ({
  innerHeight,
  innerWidth,
  series,
  gradientStops,
  getColorForValue,
}: CustomGradientLayerProps) => {
    const points = series[0].data.map((d: any) => ({
        x: d.position.x,
        y: d.position.y,
        value: d.data.y,
    }));

    //Smooth curve through the points
    const d3Path = d3Line<{ x: number; y: number }>()
        .x(d => d.x)
        .y(d => d.y)
        .curve(curveMonotoneX);

    const fullPath = d3Path(points);
    if (!fullPath) return null;

    // Define a closed path to use as clipPath for the filled area
    const closedPath = `${fullPath} L ${points[points.length - 1].x},${innerHeight} L ${points[0].x},${innerHeight} Z`;

    return (
        <>
        <defs>
            {/* Vertical gradient for the area under the curve */}
            <linearGradient id="gradientArea" x1="0" y1="1" x2="0" y2="0">
            {gradientStops.map((stop, i) => (
                <stop key={i} offset={stop.offset} stopColor={stop.color} stopOpacity="1" />
            ))}
            </linearGradient>

            {/* Horizontal gradient along the curve line */}
            <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
            {points.map((point: { value: number; x: number }, i: Key | null | undefined) => {
                const color = getColorForValue(point.value);
                const offset = (point.x / innerWidth) * 100;
                return (
                <stop
                    key={i}
                    offset={`${offset}%`}
                    stopColor={color}
                    stopOpacity="1"
                />
                );
            })}
            </linearGradient>

            {/* ClipPath to restrict the gradient area to below the curve */}
            <clipPath id="areaClipPath">
            <path d={closedPath} />
            </clipPath>
        </defs>

        {/* Filled area with vertical gradient */}
        <rect
            x={0}
            y={0}
            width="100%"
            height={innerHeight}
            fill="url(#gradientArea)"
            clipPath="url(#areaClipPath)"
            opacity={0.5}
        />

        {/* Curve line with horizontal gradient */}
        <path
            d={fullPath}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth={2}
        />
        </>
    );
};